/**
 * Composites the whole timeline into one target canvas, one frame at a time.
 *
 * Outside a transition the showing scene draws straight into the target.
 * Inside a transition window, scene A renders at its local time into
 * `aCanvas`, scene B into `bCanvas`, and the transition renderer blends them
 * into the target. Shared by the export loop and ScenePreviewOverlay, so the
 * preview and the file are the same pixels at the same timestamp.
 */

import type { ProjectPage, VideoProject } from '@canvas/shared-types';
import { isHardCut } from '@/data/transitions';
import { ease } from './easing';
import { abortError } from './exportErrors';
import type { Size } from './frameGeometry';
import { createSceneRenderer, type SceneRenderer } from './sceneCompositor';
import { activeTransitionAt, buildCues, cueAt, sceneLocalMs } from './sceneTimeline';
import { TRANSITION_RENDERERS } from './transitions';
import { createClipFrameCompositor } from './clipCompositor';

/**
 * Most scenes kept loaded at once: the current scene and the one it is
 * transitioning into. This is THE ONLY THING standing between a 60-scene
 * project and an OOM; every other renderer is disposed as soon as it falls
 * out of the window.
 */
export const SCENE_RETENTION = 2;

export interface FrameCompositor {
  /** Total timeline length. */
  readonly durationMs: number;
  /**
   * Draw the timeline at `ms` into the target canvas. ASYNC solely because
   * entering a scene loads it (and, from Phase 5, because a scene containing
   * a CraftaraVideo must seek its <video> and await the painted frame) — that
   * is the whole reason export is an awaited loop rather than a tight
   * synchronous one.
   */
  renderFrameAt(ms: number): Promise<void>;
  /** Swap the project without reloading media. The monitor uses this while dragging. */
  replaceProject?(video: VideoProject): void;
  /** Swap page JSON. Scene renderers reload only when a page's picture changed. */
  setPages?(pages: ProjectPage[]): void;
  /** De-duplicated user-facing notes from every scene loaded so far. */
  warnings(): string[];
  dispose(): void;
}

function make2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) throw new Error('This browser can’t draw video frames (no 2D canvas).');
  return ctx;
}

function scratch(out: Size): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = out.width;
  c.height = out.height;
  return c;
}

export async function createFrameCompositor(args: {
  pages: ProjectPage[];
  video: VideoProject;
  target: HTMLCanvasElement;
  signal?: AbortSignal;
}): Promise<FrameCompositor> {
  if (args.video.v === 2 && args.video.clips && args.video.tracks && args.video.canvas) {
    return createClipFrameCompositor(args);
  }
  const { video, target, signal } = args;
  const out: Size = { width: target.width, height: target.height };
  const scenes = video.scenes;
  const cues = buildCues(scenes);
  const pagesById = new Map(args.pages.map((p) => [p.pageId, p]));
  const durationMs = cues.length ? cues[cues.length - 1].endMs : 0;

  const outCtx = make2d(target);
  const aCanvas = scratch(out);
  const bCanvas = scratch(out);
  const aCtx = make2d(aCanvas);
  const bCtx = make2d(bCanvas);

  const renderers = new Map<string, Promise<SceneRenderer>>();
  /** Renderers are evicted as the timeline advances; their warnings must outlive them. */
  const seenWarnings = new Set<string>();
  const loaded = new Set<SceneRenderer>();
  let disposed = false;

  const disposeRenderer = (pageId: string) => {
    const pending = renderers.get(pageId);
    renderers.delete(pageId);
    void pending
      ?.then((r) => {
        r.warnings.forEach((w) => seenWarnings.add(w));
        loaded.delete(r);
        r.dispose();
      })
      .catch(() => undefined);
  };

  /**
   * Keep exactly `keep` loaded (starting any missing loads), evict the rest,
   * and return the pending renderers in `keep` order.
   */
  const retain = (keep: string[]): Array<Promise<SceneRenderer>> => {
    if (keep.length > SCENE_RETENTION) throw new Error('Scene retention exceeded');
    for (const id of Array.from(renderers.keys())) {
      if (!keep.includes(id)) disposeRenderer(id);
    }
    return keep.map((pageId) => {
      let pending = renderers.get(pageId);
      if (!pending) {
        const index = scenes.findIndex((s) => s.pageId === pageId);
        const page = pagesById.get(pageId);
        if (index < 0 || !page) {
          return Promise.reject(new Error('A scene points at a page that no longer exists.'));
        }
        const loading = createSceneRenderer({ page, scene: scenes[index], out, signal });
        pending = loading;
        renderers.set(pageId, loading);
        loading
          .then((r) => {
            if (renderers.get(pageId) === loading) loaded.add(r);
          })
          .catch(() => undefined);
        // A failed load must not poison the cache for a retry.
        loading.catch(() => {
          if (renderers.get(pageId) === loading) renderers.delete(pageId);
        });
      }
      return pending;
    });
  };

  const paint = async (
    ctx: CanvasRenderingContext2D,
    renderer: SceneRenderer,
    localMs: number
  ): Promise<void> => {
    const el = await renderer.renderAt(localMs);
    if (renderer.fitMode === 'contain') {
      ctx.fillStyle = renderer.bgColor;
      ctx.fillRect(0, 0, out.width, out.height);
    }
    const { x, y, width, height } = renderer.fit;
    ctx.drawImage(el, x, y, width, height);
  };

  return {
    durationMs,

    async renderFrameAt(ms) {
      if (disposed) throw new Error('Compositor used after dispose');
      if (signal?.aborted) throw abortError();
      if (!cues.length) {
        outCtx.fillStyle = '#000000';
        outCtx.fillRect(0, 0, out.width, out.height);
        return;
      }

      const tr = activeTransitionAt(cues, scenes, ms);
      if (tr && !isHardCut(tr.transition.id)) {
        const [a, b] = await Promise.all(retain([tr.from.pageId, tr.to.pageId]));
        await paint(aCtx, a, sceneLocalMs(tr.from, ms));
        await paint(bCtx, b, sceneLocalMs(tr.to, ms));
        const p = ease(tr.transition.easing ?? 'ease-in-out', Math.min(1, Math.max(0, tr.progress)));
        outCtx.save();
        TRANSITION_RENDERERS[tr.transition.id](outCtx, aCanvas, bCanvas, p, {
          width: out.width,
          height: out.height,
          direction: tr.transition.direction,
          color: tr.transition.color,
        });
        outCtx.restore();
        return;
      }

      const cue = cueAt(cues, ms);
      if (!cue) return;
      // Warm the next scene in the second retention slot so its transition doesn't stall.
      const next = cues[cue.index + 1];
      const [current, upcoming] = retain(next ? [cue.pageId, next.pageId] : [cue.pageId]);
      upcoming?.catch(() => undefined);
      await paint(outCtx, await current, sceneLocalMs(cue, ms));
    },

    warnings() {
      for (const r of loaded) r.warnings.forEach((w) => seenWarnings.add(w));
      return Array.from(seenWarnings);
    },

    dispose() {
      if (disposed) return;
      disposed = true;
      for (const id of Array.from(renderers.keys())) disposeRenderer(id);
      for (const c of [aCanvas, bCanvas]) {
        c.width = 0;
        c.height = 0;
      }
    },
  };
}
